'use client'

import NeedRecommend from '@/app/[locale]/(home)/_components/Header/_components/CartPopover/NeedRecommend'
import CardRow from '@/app/[locale]/(universal)/brand/_components/Single/components/cart/_components/CardRow'
import useAutoAddingProduct from '@/_hooks/useAutoAddingProduct'
import { useCustomLocale } from '@/_hooks/useCustomLocale'
import productService from '@/_service/product.service'
import { CLEAR } from '@/_store/cart/cart.slice'
import { IRedux } from '@/_store/types'
import { IProduct } from '@/entities/product/model/types'
import { getPlatform } from '@/_utils/platform'
import { Box, Center, Stack, StackSeparator, Text } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useActiveBrand } from '@/_hooks/useActiveBrand'
import ConfirmModal from '@/_components/ConfirmModal'
import CartProductsSkeleton from './CartProductsSkeleton'
import useDiscount from '../_hooks/useDiscount'
import { BRAND_NAMES } from '@/_utils/Constants'

export default function Products() {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const { user, cartStore, branches } = useSelector(
    (state: IRedux) => ({
      user: state.auth.user,
      cartStore: state.cart.cart,
      branches: state.common.branches,
    }),
    shallowEqual,
  )

  const t = useTranslations()
  const dispatch = useDispatch()
  const currentLocale = useCustomLocale()
  const shipperId = useActiveBrand()
  const branch = branches?.[shipperId]
  const currentBrandName = BRAND_NAMES?.[shipperId]
  const cart = cartStore?.[shipperId]?.products || []

  const { productIds, recommendProducts } = useAutoAddingProduct({ cart })

  const { data: productData, isLoading } = useQuery({
    queryKey: ['product-all-cart', productIds],
    queryFn: () =>
      productService
        .getAll(
          {
            page: 1,
            limit: 50,
            fields: 'modifiers,categories,weight,measurement',
            client_id: user?.id || '',
            menu_id: branch?.menu_id || undefined,
            product_ids: productIds,
          },
          shipperId,
        )
        .then((res) => res?.data),
    enabled: cart?.length > 0,
    select: (res) => {
      const transformed = res?.products?.reduce(
        (acc: Record<string, IProduct>, item) => {
          acc[item.id] = item
          return acc
        },
        {},
      )

      return transformed || {}
    },
    // Keep previous data while fetching new data to avoid spinner flicker
    placeholderData: (previousData) => previousData,
    staleTime: 30000, // 30 seconds - data is fresh for 30s
    gcTime: 5 * 60 * 1000, // 5 minutes - cache for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
  })

  const source = getPlatform()

  const { discounts } = useDiscount(true, source)

  // Get active brand name using hook

  const handleClearCart = () => {
    if (currentBrandName) {
      dispatch(CLEAR({ shipper_id: shipperId }))
    }
    setIsDeleteModalOpen(false)
  }

  useEffect(() => {
    if (productData && !Object.keys(productData)?.length) {
      dispatch(CLEAR({ shipper_id: shipperId }))
    }
  }, [productData, shipperId, dispatch])

  // Only show skeleton on initial load when there's no data yet
  if (isLoading && !productData) {
    return <CartProductsSkeleton />
  }

  if (!cart?.length || !productData) {
    return (
      <>
        <Center flexDir={'column'} alignItems='center' justifyContent='center'>
          <Text fontSize='lg' fontWeight='bold' textAlign='center'>
            {t('your_cart_empty')}
          </Text>
          <Text fontSize={'sm'} maxW={'280px'} textAlign={'center'}>
            {t('the_items_you_order_will_appear_here')}
          </Text>
        </Center>
      </>
    )
  }

  return (
    <>
      <Stack
        p={4}
        pt={0}
        mt={3}
        gap={3}
        overflowY='auto'
        separator={<StackSeparator borderColor={'#EAECF0'} />}
      >
        {cart?.map((item) => {
          const product = productData?.[item?.product_id]

          return (
            <CardRow
              shipperId={shipperId}
              isBrand={true}
              key={item.key}
              cartItem={item}
              data={product}
            />
          )
        })}
        {discounts?.map(
          (el, idx) =>
            el?.discount_mode === 'bonus_product' &&
            el?.bonus_product?.id && (
              <CardRow
                shipperId={shipperId}
                isBrand={true}
                index={idx + 1}
                cartItem={{
                  image: el?.bonus_product?.image || '',
                  key: '',
                  price: 0,
                  comment: '',
                  variants: {},
                  type: 'simple',
                  product_name: '',
                  category_name: '',
                  discount_percent: 0,
                  order_modifiers: [],
                  price_with_discount: 0,
                  in_stop: false,
                  default_product: false,
                  product_id: '',
                  quantity: el?.bonus_product?.count || 1,
                  addition_type: 2,

                  shipper_id: shipperId,
                  source_id: source,
                  branch_id: branch?.id || '',
                  menu_id: branch?.menu_id || '',
                }}
                bonus_product={{
                  is_bonus: true,
                  product: el?.bonus_product,
                }}
                price={el?.bonus_product?.out_price}
                key={el?.discount_id}
                title={el?.bonus_product?.title?.[currentLocale]}
                readOnly={true}
              />
            ),
        )}
      </Stack>
      <Box p={4} pt={0}>
        {Boolean(recommendProducts && recommendProducts?.length > 0) && (
          <NeedRecommend
            products={recommendProducts}
            productData={productData}
            shipperId={shipperId}
            menuId={branch?.menu_id || ''}
            branchId={branch?.id || ''}
            sourceId={source}
          />
        )}
      </Box>
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleClearCart}
        title={t('attention') || 'Attention'}
        description={
          t('are_you_sure_clear_cart') ||
          'Are you sure you want to clear the cart?'
        }
        isWarning={true}
        text={{
          confirm: 'yes',
          cancel: 'no',
        }}
      />
    </>
  )
}
